/** Small display formatters for the admin UI. Pure + unit-tested. */

/**
 * A response/link scope key is `brand#bmu#country` (empty/`-` segments mean "not set", e.g. a
 * BMU-level scope is `tbc#tb-uk#-`). Render it as a readable trail: `tbc · tb-uk`.
 */
export function formatScope(scope: string): string {
  return scope
    .split('#')
    .map((s) => s.trim())
    .filter((s) => s !== '' && s !== '-')
    .join(' · ');
}

/** ISO timestamp → "18 Jun 2026, 23:28" (en-GB). Falls back to the raw string if unparseable. */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * Compact relative time: "Just now", "5m ago", "3h ago", "2d ago", "4w ago", "5mo ago", "1y ago".
 * `undefined`/empty → "Never"; unparseable → "—". `now` is injectable for testing.
 */
export function timeAgo(iso?: string, now: number = Date.now()): string {
  if (!iso) return 'Never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return 'Just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}
